import React, {
   useCallback,
   useEffect,
   useMemo,
   useRef,
   useState,
} from 'react';
import { toast } from 'react-toastify';
import {
   LuAlertCircle,
   LuColumns,
   LuEye,
   LuList,
   LuSearch,
   LuSettings,
} from 'react-icons/lu';

import usePermissions from '../../../components/Menu/MenuItems/hooks/usePermissions';
import {
   escalateDunning,
   listDunningAssignees,
   moveDunningStage,
   returnDunning,
} from '../../../services/finance/dunningApi';
import { financeErrorMessage } from '../utils/apiError';

import {
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../../styles/layout/PageHeader';
import {
   Card,
   EmptyText,
   EmptyTitle,
   EmptyWrap,
   SecondaryButton,
} from '../../../styles/dunning/base';
import {
   HeaderActions,
   HeaderRow,
   KpiLabel,
   KpiTile,
   KpiValue,
   PageWrap,
   ShellButton,
} from '../../../styles/dunning/shell';
import {
   DisplayToggle,
   FilterSelect,
   FiltersCard,
   FiltersSpacer,
   PendingToggle,
   SearchGoButton,
   SearchInput,
   SearchWrap,
   SegmentedItem,
   SegmentedTrack,
} from '../../../styles/dunning/filters';

import useDunningFilters from './hooks/useDunningFilters';
import useDunningList from './hooks/useDunningList';
import useDunningCase from './hooks/useDunningCase';
import useStages from './hooks/useStages';
import {
   COMMIT_FILTER_OPTIONS,
   STATUS_KPI_LABELS,
   STATUS_TABS,
   VIEW_TABS,
} from './constants';
import { canMoveCase, caseFigures, isExternalTrack } from './utils/derive';
import { formatBRLShort, todayIso } from '../utils/dunningFormat';

import KanbanBoard from './components/KanbanBoard';
import QueueTable from './components/QueueTable';
import CaseDrawer from './components/CaseDrawer';
import AssignModal from './components/AssignModal';
import CommitModal from './components/CommitModal';
import TransferChargesModal from './components/TransferChargesModal';
import StagesModal from './components/StagesModal';

// ---------------------------------------------------------------------------
// Financeiro › Cobranças › Vencidas — Régua de Cobrança (handoff 0090).
//
// CRM kanban de títulos vencidos: caso = CLIENTE (todos os títulos vencidos
// dele). O caso nasce por webhook (PAYMENT_OVERDUE) e é baixado por webhook de
// pagamento — aqui não existe "criar caso" nem "dar baixa no caso"; o operador
// trabalha o meio do funil: move de raia, registra tratativa, agenda
// compromisso, escala para cobrança administrativa.
//
// Um fetch alimenta kanban E fila (trocar de visão não refaz request). URL
// espelha filtros/visão/caso aberto com `replace: true` (deep-link e F5
// restauram o contexto — decisão de navegação ratificada em 2026-08-14).
//
// RBAC fatiado (§6, handoff 0100 — 2026-08-24): rota gateada por
// finance:dunning:read. O trabalho do dia a dia (tratativa, anexo, compromisso)
// é `interact`; `update` virou GESTÃO — trocar responsável e mover raia (drag e
// "registrar e avançar"). `external` gateia a escalada e `dunning_stage:manage`
// o botão Raias. Elementos SOMEM sem permissão, não desabilitam.
//
// ⚠️ Este arquivo pressupõe o seed do 0100 rodado no backend: sem ele ninguém
// tem `interact` e a tela operacional se apaga. Deploy é coordenado (seed →
// backend → front).
//
// Escopo por atribuição (Feature 1, 2026-08-17): sem finance:dunning:read_all
// o backend força a lista para os casos atribuídos ao usuário e veta mexer em
// assigned_user_id (403 assignment_change_forbidden). O front acompanha:
// esconde filtro/campo de responsável e NÃO envia a chave no PATCH.
// ---------------------------------------------------------------------------

// Busca casa nome OU documento — documento vai SEM máscara (guia §3).
function normalizeSearch(raw) {
   const value = raw.trim();
   if (!value) return '';
   if (/^[\d./\s-]+$/.test(value)) return value.replace(/\D/g, '');
   return value;
}

// Teto do polling pós-baixa (5s × 24 = 2min); depois o refetch-no-foco cobre.
const AWAITING_POLL_MS = 5000;
const AWAITING_POLL_MAX_TICKS = 24;

const VIEW_ICONS = {
   kanban: LuColumns,
   fila: LuList,
};

export default function Dunning() {
   const { hasPermission } = usePermissions();
   // Operação: registrar tratativa, anexar/excluir anexo, definir compromisso.
   const canInteract = hasPermission('finance:dunning:interact');
   // Gestão: trocar responsável e mover raia (drag e "registrar e avançar").
   const canUpdate = hasPermission('finance:dunning:update');
   // Trilha: enviar título ao escritório e devolvê-lo. A mesma chave para os
   // dois sentidos — quem decide entregar decide retomar (0103 §5.2).
   const canExternal = hasPermission('finance:dunning:external');
   // Acerto (0103 §7): conferir título e ler/exportar o relatório. Separada de
   // `:interact` de propósito — quem trata o caso não confere o próprio
   // trabalho. Concedida só ao Master pelo seed.
   const canReconcile = hasPermission('finance:dunning:reconcile');
   const canManageStages = hasPermission('finance:dunning_stage:manage');
   // Baixa manual é operação do módulo billing — permissão própria, separada
   // das da régua. Sem ela o botão de baixa nem aparece no drawer.
   const canSettle = hasPermission('finance:charge:settle');
   // Sem read_all o usuário é RESTRITO ao próprio escopo (Feature 1) — gateia o
   // filtro de responsável na listagem.
   const canReadAll = hasPermission('finance:dunning:read_all');
   // Reatribuir exige as DUAS: gestão (update) e enxergar caso alheio
   // (read_all). Restrito com update continua sem reatribuir — o backend
   // responde 403 assignment_change_forbidden.
   const canAssign = canReadAll && canUpdate;

   const today = useMemo(() => todayIso(), []);

   const filters = useDunningFilters();
   const {
      rows,
      stageCounts,
      meta,
      loading: listLoading,
      error: listError,
      refetch: refetchList,
      patchCase,
   } = useDunningList({
      filters: filters.listFilters,
      // Restrito não vê o filtro de responsável — o server já força o escopo;
      // qualquer 'none'/id herdado da URL viraria pós-filtro fantasma.
      assignee: canReadAll ? filters.assignee : 'all',
      commitFilter: filters.commit,
      today,
   });
   const {
      stages,
      activeStages,
      loading: stagesLoading,
      refetch: refetchStages,
   } = useStages();
   const caseHook = useDunningCase(filters.openCaseId);

   // ─── Candidatos a responsável (filtro + modais de atribuição) ───────────
   // Fonte própria do módulo desde 0100 (§3.2): GET /billing-dunnings/assignees
   // devolve SÓ quem tem `finance:dunning:assignable`. Substituiu o GET /users
   // — que trazia inelegíveis (422 user_not_assignable no save) e amarrava a
   // régua à permissão de listar usuários.
   //
   // Restrito não atribui nem filtra por responsável → nem busca a lista.
   const [assignees, setAssignees] = useState([]);
   useEffect(() => {
      if (!canReadAll) return;
      listDunningAssignees()
         .then(setAssignees)
         .catch(() => {
            // Sem a lista, os selects de responsável ficam só com as opções
            // fixas e a tela segue operável — é o que acontece enquanto o
            // endpoint do 0100 não estiver no ar.
         });
   }, [canReadAll]);

   // ─── Rascunho × aplicado (carregamento sob demanda, decisão 2026-08-14) ─
   // Os controles editam SÓ o rascunho; nada dispara request até o operador
   // confirmar no botão Pesquisar (ou Enter na busca). O botão só habilita
   // quando o rascunho difere do recorte aplicado.
   const [draft, setDraft] = useState({
      search: filters.search,
      status: filters.status,
      assignee: filters.assignee,
      commit: filters.commit,
      onlyPending: filters.onlyPending,
   });

   const draftSearch = normalizeSearch(draft.search);
   const isDraftDirty =
      draftSearch !== filters.search ||
      draft.status !== filters.status ||
      draft.assignee !== filters.assignee ||
      draft.commit !== filters.commit ||
      draft.onlyPending !== filters.onlyPending;

   const handleApplyFilters = useCallback(() => {
      filters.applyFilters({
         search: draftSearch,
         status: draft.status,
         assignee: draft.assignee,
         commit: draft.commit,
         onlyPending: draft.onlyPending,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [filters.applyFilters, draftSearch, draft]);

   // ─── Drag & drop nativo (padrão do board do Routing) ────────────────────
   const [dragCaseId, setDragCaseId] = useState(null);
   const [dropStageId, setDropStageId] = useState(null);

   // ─── Modais de escopo da tela ───────────────────────────────────────────
   const [showTransfer, setShowTransfer] = useState(false);
   const [isTransferring, setIsTransferring] = useState(false);
   const [showCommit, setShowCommit] = useState(false);
   const [showStages, setShowStages] = useState(false);
   // O caso que o modal de responsável está editando. É estado PRÓPRIO, não
   // `caseHook.caseData`, porque a atribuição também é aberta logo após uma
   // transferência — e nesse instante o drawer está trocando de caso (o hook
   // zera `caseData` enquanto busca o destino). Com o caso explícito, o modal
   // nunca depende de qual caso o drawer terminou de carregar.
   const [assignCase, setAssignCase] = useState(null);

   // ─── Baixa manual não-otimista: títulos aguardando webhook ──────────────
   const [awaitingCharges, setAwaitingCharges] = useState({});
   const pollTicksRef = useRef(0);

   // Trava o scroll do fundo enquanto o drawer está aberto.
   useEffect(() => {
      if (!filters.openCaseId) return undefined;
      const previous = document.body.style.overflow || '';
      document.body.style.overflow = 'hidden';
      return () => {
         document.body.style.overflow = previous;
      };
   }, [filters.openCaseId]);

   // Toda mutação devolve o caso inteiro: aplica na lista e no drawer na hora
   // e reconcilia com um refetch silencioso (ordenação/filtros são do server).
   const applyCaseUpdate = useCallback(
      (updatedCase) => {
         if (!updatedCase?.id) return;
         patchCase(updatedCase);
         if (filters.openCaseId === updatedCase.id) {
            caseHook.setCaseData(updatedCase);
            caseHook.refetchEvents();
         }
         refetchList({ quiet: true });
      },
      [patchCase, filters.openCaseId, caseHook, refetchList]
   );

   // ─── Movimentação de raia (drag ou confirmação da escalada) ─────────────
   const moveCase = useCallback(
      async (caseId, targetStage) => {
         const row = rows.find((item) => item.id === caseId);
         if (!row) return;

         if (row.status !== 'OPEN') {
            toast.error('Caso fechado não pode ser movido.');
            return;
         }
         // Trilha externa não muda de raia (0103 §3.2) — o card nem é
         // arrastável, isto é a rede de segurança do drop.
         if (isExternalTrack(row)) {
            toast.error(
               'Caso em cobrança administrativa não muda de raia. Devolva os títulos à régua interna primeiro.'
            );
            return;
         }
         if (targetStage.stage_type === 'TERMINAL_PAID') {
            toast.error(
               'A raia Recebido é automática — só o pagamento move o caso para lá.'
            );
            return;
         }
         if (targetStage.stage_type === 'EXTERNAL') {
            // Deixou de ser destino de movimentação: o que atravessa a
            // fronteira é o TÍTULO, escolhido um a um no fluxo "Enviar ao
            // escritório" (0103 §3.1). Antes, o drop aqui escalava o caso
            // inteiro — e todo título que vencesse depois caía junto no
            // escritório sem nunca ser tentado pela casa.
            toast.error(
               'Para enviar ao escritório, abra o caso e escolha os títulos em "Enviar ao escritório".'
            );
            return;
         }
         if (row.stage?.id === targetStage.id) {
            toast.error('O caso já está nessa raia.');
            return;
         }

         // Otimista com reversão (guia §5.2): o card troca de coluna já; se o
         // PATCH falhar, volta e o toast diz o motivo.
         const snapshot = row;
         patchCase({
            ...row,
            stage: {
               id: targetStage.id,
               name: targetStage.name,
               stage_type: targetStage.stage_type,
               position: targetStage.position,
            },
         });

         try {
            const updated = await moveDunningStage(caseId, targetStage.id);
            applyCaseUpdate(updated);
            toast.success(`Caso movido para ${targetStage.name}.`);
         } catch (err) {
            patchCase(snapshot);
            toast.error(
               financeErrorMessage(err, 'Não foi possível mover o caso.')
            );
         }
      },
      [rows, patchCase, applyCaseUpdate]
   );

   const handleDropOnStage = useCallback(
      (stage) => {
         const caseId = dragCaseId;
         setDragCaseId(null);
         setDropStageId(null);
         if (!caseId || !canUpdate) return;
         moveCase(caseId, stage);
      },
      [dragCaseId, canUpdate, moveCase]
   );

   // ─── Transferência de títulos entre trilhas (0103) ──────────────────────
   // Escalar e devolver são a MESMA operação em sentidos opostos, e as duas
   // respondem o caso de DESTINO — nunca o da URL.
   //
   // Três consequências que o fluxo antigo (escalar o caso inteiro) não tinha:
   //  1. o caso de ORIGEM mudou e NÃO vem na resposta (perdeu títulos, e pode
   //     ter encerrado como TRANSFERRED) → o refetch da lista é obrigatório;
   //  2. o gestor continua trabalhando no destino → o drawer segue para lá;
   //  3. caso externo NASCE sem responsável e o escritório (sem `read_all`) só
   //     enxerga o que é dele — então, se o destino é novo e sem responsável, a
   //     atribuição é oferecida na hora (§9), em vez de virar trabalho parado
   //     que ninguém vê.
   const handleConfirmTransfer = useCallback(
      async ({ direction, chargeIds }) => {
         const sourceId = caseHook.caseData?.id;
         if (!sourceId) return;
         const isReturn = direction === 'RETURN';

         setIsTransferring(true);
         try {
            const destination = isReturn
               ? await returnDunning(sourceId, chargeIds)
               : await escalateDunning(sourceId, chargeIds);

            patchCase(destination);
            refetchList({ quiet: true });
            toast.success(
               isReturn
                  ? 'Títulos devolvidos à régua interna.'
                  : 'Títulos enviados ao escritório.'
            );
            setShowTransfer(false);

            if (destination?.id) filters.openCase(destination.id);
            if (!destination?.assigned_user && canAssign) {
               setAssignCase(destination);
            }
         } catch (err) {
            // Modal segue aberto: a seleção de títulos não se perde.
            toast.error(
               financeErrorMessage(
                  err,
                  isReturn
                     ? 'Não foi possível devolver os títulos.'
                     : 'Não foi possível enviar os títulos ao escritório.'
               )
            );
         } finally {
            setIsTransferring(false);
         }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [caseHook.caseData, patchCase, refetchList, filters.openCase, canAssign]
   );

   // ─── Aguardando webhook: polling curto até o Asaas refletir ─────────────
   const markAwaiting = useCallback((chargeId) => {
      pollTicksRef.current = 0;
      setAwaitingCharges((prev) => ({ ...prev, [chargeId]: true }));
   }, []);

   const awaitingCount = Object.keys(awaitingCharges).length;

   useEffect(() => {
      if (!awaitingCount || !filters.openCaseId) return undefined;
      const timer = window.setInterval(() => {
         pollTicksRef.current += 1;
         if (pollTicksRef.current > AWAITING_POLL_MAX_TICKS) {
            window.clearInterval(timer);
            return;
         }
         caseHook.refetch({ quiet: true });
         refetchList({ quiet: true });
      }, AWAITING_POLL_MS);
      return () => window.clearInterval(timer);
      // caseHook.refetch é estável por caso aberto.
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [awaitingCount, filters.openCaseId]);

   // Webhook chegou (membro saiu de OPEN) → título deixa de "aguardar".
   useEffect(() => {
      const members = caseHook.caseData?.members;
      if (!members || !awaitingCount) return;
      const settledIds = members
         .filter(
            (member) =>
               awaitingCharges[member.charge_id] &&
               member.member_status !== 'OPEN'
         )
         .map((member) => member.charge_id);
      if (!settledIds.length) return;
      setAwaitingCharges((prev) => {
         const next = { ...prev };
         settledIds.forEach((id) => delete next[id]);
         return next;
      });
   }, [caseHook.caseData, awaitingCharges, awaitingCount]);

   // ─── KPIs (somados no cliente sobre a página corrente — lacuna §8.1) ────
   // Acompanham a aba de status (0102): caso encerrado tem `open_*` zerado por
   // definição, então em Recebidos/Cancelados os tiles falavam de um bloco que
   // não existe e mostravam zero. O corte de valor é o mesmo dos cards
   // (`caseFigures`); só os rótulos e o terceiro tile mudam.
   const kpis = useMemo(() => {
      const scoped = rows.filter((row) => row.status === filters.status);
      const totals = scoped.reduce(
         (acc, row) => {
            const figures = caseFigures(row);
            acc.amount += figures.amount;
            acc.count += figures.count;
            return acc;
         },
         { amount: 0, count: 0 }
      );
      const isOpenTab = filters.status === 'OPEN';
      return {
         cases: scoped.length,
         amount: totals.amount,
         // Em cobrança: fila de ação imediata (destaque vermelho). Encerrado:
         // quantos títulos compõem o valor ao lado.
         third: isOpenTab
            ? scoped.filter((row) => row.action_status === 'PENDING').length
            : totals.count,
         alert: isOpenTab,
      };
   }, [rows, filters.status]);

   const kpiLabels = STATUS_KPI_LABELS[filters.status] ?? STATUS_KPI_LABELS.OPEN;

   const hasMorePages = (meta?.totalPages ?? 1) > 1;

   // ─── Raias vazias ocultas por default (Feature 2, 2026-08-17) ───────────
   // Vazia = fora do stage_counts do server (mesmos filtros, sem paginação —
   // imune à lacuna §8.1). Fallback pro backend sem o campo: decide pela
   // página carregada. Consequência assumida: raia oculta não é alvo de drag —
   // mover pra raia vazia exige ligar o toggle ou usar "Avançar para a raia".
   const visibleStages = useMemo(() => {
      if (filters.showEmpty) return activeStages;
      if (stageCounts) {
         const nonEmptyIds = new Set(
            stageCounts
               .filter((count) => (count.total ?? 0) > 0)
               .map((count) => count.stage_id)
         );
         return activeStages.filter((stage) => nonEmptyIds.has(stage.id));
      }
      const idsWithRows = new Set(rows.map((row) => row.stage?.id));
      return activeStages.filter((stage) => idsWithRows.has(stage.id));
   }, [filters.showEmpty, activeStages, stageCounts, rows]);

   // Miolo da tela: erro de carga → visões (kanban/fila) sobre a MESMA lista.
   let content;
   if (listError && !rows.length && !listLoading) {
      content = (
         <Card>
            <EmptyWrap>
               <EmptyTitle>Algo deu errado</EmptyTitle>
               <EmptyText>Falha ao carregar os casos.</EmptyText>
               <div style={{ marginTop: 12 }}>
                  <SecondaryButton type="button" onClick={() => refetchList()}>
                     Tentar novamente
                  </SecondaryButton>
               </div>
            </EmptyWrap>
         </Card>
      );
   } else if (
      filters.view === 'kanban' &&
      !listLoading &&
      !stagesLoading &&
      visibleStages.length === 0
   ) {
      // Todas as raias vazias no recorte (comum pro restrito sem caso).
      content = (
         <Card>
            <EmptyWrap>
               <EmptyTitle>Nenhum caso no recorte atual</EmptyTitle>
               <EmptyText>
                  Nenhuma raia tem casos com os filtros aplicados. Ative
                  &quot;Exibir raias vazias&quot; para ver o quadro completo.
               </EmptyText>
            </EmptyWrap>
         </Card>
      );
   } else if (filters.view === 'kanban') {
      content = (
         <KanbanBoard
            stages={visibleStages}
            rows={rows}
            today={today}
            statusFilter={filters.status}
            loading={listLoading || stagesLoading}
            hasMorePages={hasMorePages}
            dragCaseId={canUpdate ? dragCaseId : null}
            dropStageId={dropStageId}
            onDragStart={(caseId) => {
               // Dupla checagem do que o card já bloqueia: gestão (canUpdate) e
               // caso movível (aberto e da trilha interna — 0103 §3.2).
               const row = rows.find((item) => item.id === caseId);
               if (canUpdate && canMoveCase(row)) setDragCaseId(caseId);
            }}
            onDragEnd={() => {
               setDragCaseId(null);
               setDropStageId(null);
            }}
            onDragOverStage={setDropStageId}
            onDragLeaveStage={(stageId) => {
               setDropStageId((prev) => (prev === stageId ? null : prev));
            }}
            onDropOnStage={handleDropOnStage}
            onOpenCase={filters.openCase}
         />
      );
   } else {
      content = (
         <QueueTable
            rows={rows}
            today={today}
            statusFilter={filters.status}
            loading={listLoading}
            hasMorePages={hasMorePages}
            sortConfig={filters.sort}
            onSortChange={filters.setSort}
            onOpenCase={filters.openCase}
         />
      );
   }

   return (
      <PageWrap>
         <HeaderRow>
            <PageHeaderText>
               <PageTitle>Cobranças vencidas</PageTitle>
               <PageSubtitle>
                  Um caso por cliente. Arraste entre as raias ou abra o caso
                  para registrar tratativa.
               </PageSubtitle>
            </PageHeaderText>
            <HeaderActions>
               <KpiTile>
                  <KpiLabel>{kpiLabels.cases}</KpiLabel>
                  <KpiValue>{listLoading ? '—' : kpis.cases}</KpiValue>
               </KpiTile>
               <KpiTile>
                  <KpiLabel>{kpiLabels.amount}</KpiLabel>
                  <KpiValue>
                     {listLoading ? '—' : formatBRLShort(kpis.amount)}
                  </KpiValue>
               </KpiTile>
               <KpiTile>
                  <KpiLabel>{kpiLabels.third}</KpiLabel>
                  <KpiValue $alert={kpis.alert}>
                     {listLoading ? '—' : kpis.third}
                  </KpiValue>
               </KpiTile>
               {canManageStages && (
                  <ShellButton type="button" onClick={() => setShowStages(true)}>
                     <LuSettings size={18} />
                     Raias
                  </ShellButton>
               )}
            </HeaderActions>
         </HeaderRow>

         <FiltersCard>
            <SearchWrap>
               <LuSearch size={16} />
               <SearchInput
                  value={draft.search}
                  placeholder="Cliente ou CNPJ/CPF"
                  title="Pressione Enter ou use Pesquisar"
                  onChange={(event) => {
                     const { value } = event.target;
                     setDraft((prev) => ({ ...prev, search: value }));
                  }}
                  onKeyDown={(event) => {
                     if (event.key === 'Enter' && isDraftDirty) {
                        handleApplyFilters();
                     }
                  }}
               />
            </SearchWrap>

            <SegmentedTrack>
               {STATUS_TABS.map((tab) => (
                  <SegmentedItem
                     key={tab.value}
                     type="button"
                     $active={draft.status === tab.value}
                     onClick={() =>
                        setDraft((prev) => ({ ...prev, status: tab.value }))
                     }
                  >
                     {tab.label}
                  </SegmentedItem>
               ))}
            </SegmentedTrack>

            {canReadAll && (
               <FilterSelect
                  value={draft.assignee}
                  onChange={(event) => {
                     const { value } = event.target;
                     setDraft((prev) => ({ ...prev, assignee: value }));
                  }}
                  aria-label="Responsável"
               >
                  <option value="all">Todos os responsáveis</option>
                  <option value="none">Sem responsável</option>
                  {assignees.map((user) => (
                     <option key={user.id} value={String(user.id)}>
                        {user.nome}
                     </option>
                  ))}
               </FilterSelect>
            )}

            <FilterSelect
               value={draft.commit}
               onChange={(event) => {
                  const { value } = event.target;
                  setDraft((prev) => ({ ...prev, commit: value }));
               }}
               aria-label="Compromisso"
            >
               {COMMIT_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                     {option.label}
                  </option>
               ))}
            </FilterSelect>

            <PendingToggle
               type="button"
               $active={draft.onlyPending}
               title="Casos com ação requerida agora"
               onClick={() =>
                  setDraft((prev) => ({
                     ...prev,
                     onlyPending: !prev.onlyPending,
                  }))
               }
            >
               <LuAlertCircle size={16} />
               Só pendentes
            </PendingToggle>

            <SearchGoButton
               type="button"
               disabled={!isDraftDirty}
               title={
                  isDraftDirty
                     ? 'Aplicar os filtros'
                     : 'Os filtros já refletem o resultado exibido'
               }
               onClick={handleApplyFilters}
            >
               <LuSearch size={15} />
               Pesquisar
            </SearchGoButton>

            <FiltersSpacer />

            {/* Exibição, não filtro de dados: aplica na hora, sem Pesquisar.
                Só faz sentido no kanban (a fila não tem colunas). */}
            {filters.view === 'kanban' && (
               <DisplayToggle
                  type="button"
                  $active={filters.showEmpty}
                  title="Mostrar também as raias sem casos no recorte atual"
                  onClick={filters.toggleShowEmpty}
               >
                  <LuEye size={16} />
                  Exibir raias vazias
               </DisplayToggle>
            )}

            <SegmentedTrack>
               {VIEW_TABS.map((tab) => {
                  const ViewIcon = VIEW_ICONS[tab.value];
                  return (
                     <SegmentedItem
                        key={tab.value}
                        type="button"
                        $active={filters.view === tab.value}
                        title={tab.label}
                        onClick={() => filters.setView(tab.value)}
                     >
                        <ViewIcon size={16} />
                        {tab.label}
                     </SegmentedItem>
                  );
               })}
            </SegmentedTrack>
         </FiltersCard>

         {content}

         {filters.openCaseId && (
            <CaseDrawer
               caseData={caseHook.caseData}
               events={caseHook.events}
               loading={caseHook.loading}
               stages={stages}
               activeStages={activeStages}
               today={today}
               canInteract={canInteract}
               canUpdate={canUpdate}
               canExternal={canExternal}
               canSettle={canSettle}
               canAssign={canAssign}
               canReconcile={canReconcile}
               canReadAll={canReadAll}
               awaitingCharges={awaitingCharges}
               onClose={filters.closeCase}
               // Salto entre as pontas da transferência pela timeline (D22).
               // É o mesmo `openCase` do board: troca o caso do drawer sem
               // fechá-lo, e a URL acompanha (`?case=N`).
               onOpenCase={filters.openCase}
               onCaseUpdated={applyCaseUpdate}
               onOpenCommit={() => setShowCommit(true)}
               onOpenAssign={() => setAssignCase(caseHook.caseData)}
               onAskTransfer={() => setShowTransfer(true)}
               onSettleConfirmed={markAwaiting}
            />
         )}

         <AssignModal
            isOpen={!!assignCase}
            caseData={assignCase}
            assignees={assignees}
            onClose={() => setAssignCase(null)}
            onSaved={applyCaseUpdate}
         />

         <CommitModal
            isOpen={showCommit}
            caseData={caseHook.caseData}
            assignees={assignees}
            canAssign={canAssign}
            onClose={() => setShowCommit(false)}
            onSaved={applyCaseUpdate}
         />

         <TransferChargesModal
            isOpen={showTransfer}
            caseData={caseHook.caseData}
            isSending={isTransferring}
            onClose={() => setShowTransfer(false)}
            onConfirm={handleConfirmTransfer}
         />

         <StagesModal
            isOpen={showStages}
            stages={stages}
            occupiedStageIds={
               new Set(
                  rows
                     .filter((row) => row.status !== 'CANCELLED')
                     .map((row) => row.stage?.id)
               )
            }
            onClose={() => setShowStages(false)}
            onChanged={() => {
               refetchStages({ quiet: true });
               refetchList({ quiet: true });
            }}
         />
      </PageWrap>
   );
}
